﻿(() => {
  const parseJSON = (/** @type {string} */ jsonString) => {
    try {
      return JSON.parse(jsonString);
    } catch (error) {
      console.error('Invalid JSON:', /** @type {Error} */ (error).message);
      return null;
    }
  };

  const getGlobalValues = () => {
    const jsonNode = document.querySelector('#DataLayerGlobalValuesJSON');

    if (!jsonNode?.textContent) {
      console.error('Global values script tag is missing or empty');
      return null;
    }

    return parseJSON(jsonNode.textContent.trim());
  };

  /** @type {Object.<string, any>} */
  const globalValues = getGlobalValues();

  if (!globalValues) return;

  const PAGE_TYPE_MAP = parseJSON(globalValues.pageTypeMap);
  const SITE_SECTION_MAP = parseJSON(globalValues.siteSectionMap);
  const ADD_TO_CART_TYPE_MAP = parseJSON(globalValues.addToCartTypeMap);

  /**
   * Maps a Shopify cart line to a dataLayer item object.
   * @param {any} cartLine
   * @param {{ index?: any, item_list_id?: any, item_list_name?: any }} [listContext]
   */
  const mapCartLineToItem = (cartLine, listContext) => ({
    item_id: cartLine?.merchandise?.id || undefined,
    item_name: cartLine?.merchandise?.product?.title || undefined,
    quantity: cartLine?.quantity || undefined,
    price: cartLine?.merchandise?.price?.amount || undefined,
    affiliation: cartLine?.merchandise?.product?.type || undefined,
    coupon: undefined,
    discount: undefined,
    index: listContext?.index ?? undefined,
    item_brand: 'Bundaberg',
    item_category: 'Products',
    item_category2: undefined,
    item_category3: undefined,
    item_category4: undefined,
    item_category5: undefined,
    item_list_id: listContext?.item_list_id || undefined,
    item_list_name: listContext?.item_list_name || undefined,
    item_variant: cartLine?.merchandise?.title || undefined,
    location_id: undefined,
  });

  /**
   * Maps a Shopify product variant to a dataLayer item object (for list views).
   * @param {any} variant
   * @param {number} index
   * @param {{ item_list_id?: any, item_list_name?: any }} [listContext]
   */
  const mapVariantToItem = (variant, index, listContext) => ({
    id: variant?.product?.id || undefined,
    item_id: variant?.id || undefined,
    item_name: `${variant?.product?.title} - ${variant?.title}`.replace(' - Default Title', '') || undefined,
    quantity: 1,
    price: variant?.price?.amount || undefined,
    affiliation: variant?.product?.type || undefined,
    coupon: undefined,
    discount: undefined,
    index: index,
    item_brand: 'Bundaberg',
    item_category: 'Products',
    item_category2: undefined,
    item_category3: undefined,
    item_category4: undefined,
    item_category5: undefined,
    item_list_id: listContext?.item_list_id || undefined,
    item_list_name: listContext?.item_list_name || undefined,
    item_variant: variant?.title === 'Default Title' ? undefined : variant?.title,
    location_id: undefined,
  });

  /**
   * Strips the `id` property from each item in an array.
   * @param {any[] | undefined} items
   */
  const stripIdFromItems = (items) =>
    items?.map((/** @type {any} */ item) => {
      const { id, ...rest } = item;
      return rest;
    }) || undefined;

  const CART_LIST_CONTEXT_KEY = 'cartItemListContext';

  /**
   * Saves list context for a variant when it's added to cart.
   * Also persists to a cookie so the checkout pixel sandbox can access it.
   * @param {string} variantId
   * @param {{ index?: any, item_list_id?: any, item_list_name?: any }} context
   */
  const saveCartItemListContext = (variantId, context) => {
    try {
      const stored = sessionStorage.getItem(CART_LIST_CONTEXT_KEY);
      const map = stored ? JSON.parse(stored) : {};
      map[variantId] = context;
      const json = JSON.stringify(map);
      sessionStorage.setItem(CART_LIST_CONTEXT_KEY, json);
      document.cookie = `${CART_LIST_CONTEXT_KEY}=${encodeURIComponent(json)};path=/;SameSite=Lax`;
    } catch {
      // silent fail
    }
  };

  /**
   * Retrieves stored list context for a variant in cart.
   * @param {string} variantId
   * @returns {{ index?: any, item_list_id?: any, item_list_name?: any } | undefined}
   */
  const getCartItemListContext = (variantId) => {
    try {
      const stored = sessionStorage.getItem(CART_LIST_CONTEXT_KEY);
      if (!stored) return undefined;
      const map = JSON.parse(stored);
      return map[variantId] || undefined;
    } catch {
      return undefined;
    }
  };

  /**
   * @typedef {Object} FormatterParams
   * @property {any} eventObj
   */
  window.dataLayerFormatters({
    /**
     * @param {FormatterParams} params
     */
    page_viewed: ({ eventObj }) => {
      const countryCode = globalValues.localization?.country?.isoCode;

      const path = globalValues.request?.path || '';
      const pathSegments = path.split('/').filter(Boolean);

      /** @type {Object.<string, string>} */
      const SITE_SECTION_PATH_MAP = {
        '': 'Home',
        collections: 'Products',
        products: 'Products',
        'pages/recipes': 'Recipes',
        'blogs/recipes': 'Recipes',
        'pages/visit-us': 'Distillery',
        'pages/book-a-tour': 'Distillery',
        'pages/faq': 'FAQ',
        'pages/contact-us': 'Contact Us',
        'pages/our-story': 'Our Story',
        blogs: 'Articles',
        account: 'Account',
        cart: 'Checkout',
        404: '404',
      };

      const getSiteSection = () => {
        if (path === '/') return 'Home';
        // Check 2-segment paths first (e.g. pages/faq)
        const twoSegment = pathSegments.slice(0, 2).join('/');
        if (SITE_SECTION_PATH_MAP[twoSegment]) return SITE_SECTION_PATH_MAP[twoSegment];
        // Check 1-segment paths (e.g. collections, products)
        if (SITE_SECTION_PATH_MAP[pathSegments[0]]) return SITE_SECTION_PATH_MAP[pathSegments[0]];
        // Fallback to existing map
        return (
          Object.keys(SITE_SECTION_MAP).find((siteSection) =>
            SITE_SECTION_MAP[siteSection]
              .split(',')
              .includes(globalValues.template?.suffix || globalValues.template?.name)
          ) || undefined
        );
      };

      const getSiteSubsection = () => {
        if (path === '/') return undefined;
        // For collection pages, use collection title
        if (pathSegments[0] === 'collections' && globalValues.collection?.title) {
          return globalValues.collection.title;
        }
        // For product pages, use page title
        if (pathSegments[0] === 'products') {
          return globalValues.pageTitle || undefined;
        }
        // For other pages, use page title
        return globalValues.pageTitle || undefined;
      };

      return {
        event: 'page_view',
        timestamp: new Date(eventObj?.timestamp)?.getTime() || undefined,
        user_id: globalValues.user?.id || undefined,
        user_locale: globalValues.user?.id && countryCode ? countryCode : undefined,
        page_locale: countryCode || undefined,
        region: 'APAC',
        site_type: 'Ecommerce',
        page_type:
          Object.keys(PAGE_TYPE_MAP).find((pageType) =>
            PAGE_TYPE_MAP[pageType].split(',').includes(globalValues.template?.name)
          ) || undefined,
        site_section: getSiteSection(),
        site_subsection: getSiteSubsection(),
        brand: globalValues.brandName || 'Bundaberg',
      };
    },
    /**
     * @param {FormatterParams} params
     */
    product_viewed: ({ eventObj }) => {
      // Look up list context from the last viewed collection/search
      const lastListData = (() => {
        try {
          const stored = localStorage.getItem('lastViewedCollectionData');
          if (!stored) return null;
          const parsed = JSON.parse(stored);
          const variantId = eventObj?.data?.productVariant?.id;
          const items = parsed?.ecommerce?.items;
          if (!items || !variantId) return null;
          const match = items.find((/** @type {any} */ item) => item.item_id === variantId || item.id === variantId);
          if (!match) return null;
          return {
            index: match.index,
            item_list_id: match.item_list_id,
            item_list_name: match.item_list_name,
          };
        } catch {
          return null;
        }
      })();

      return {
        event: 'view_item',
        view_item_type: 'Ecommerce',
        timestamp: new Date(eventObj?.timestamp)?.getTime() || undefined,
        _clear: true,
        ecommerce: {
          currency: eventObj?.data?.productVariant?.price?.currencyCode || undefined,
          value: eventObj?.data?.productVariant?.price?.amount || undefined,
          items: [
            {
              item_id: eventObj?.data?.productVariant?.id || undefined,
              item_name:
                `${eventObj?.data?.productVariant?.product?.title} - ${eventObj?.data?.productVariant?.title}`.replace(
                  ' - Default Title',
                  ''
                ) || undefined,
              quantity: 1,
              price: eventObj?.data?.productVariant?.price?.amount || undefined,
              affiliation: eventObj?.data?.productVariant?.product?.type || undefined,
              coupon: undefined,
              discount: globalValues.variant?.compareAtPrice
                ? globalValues.variant?.compareAtPrice - globalValues.variant?.price
                : undefined,
              index: lastListData?.index ?? undefined,
              item_brand: 'Bundaberg',
              item_category: 'Products',
              item_category2: undefined,
              item_category3: undefined,
              item_category4: undefined,
              item_category5: undefined,
              item_list_id: lastListData?.item_list_id ?? undefined,
              item_list_name: lastListData?.item_list_name ?? undefined,
              item_variant:
                eventObj?.data?.productVariant?.title === 'Default Title'
                  ? undefined
                  : eventObj?.data?.productVariant?.title,
              location_id: globalValues.variant.locationId || undefined,
            },
          ],
        },
      };
    },
    /**
     * @param {FormatterParams} params
     */
    product_added_to_cart: ({ eventObj }) => {
      // Look up list context from the last viewed collection/search
      const lastListData = (() => {
        try {
          const stored = localStorage.getItem('lastViewedCollectionData');
          if (!stored) return null;
          const parsed = JSON.parse(stored);
          const variantId = eventObj?.data?.cartLine?.merchandise?.id;
          const items = parsed?.ecommerce?.items;
          if (!items || !variantId) return null;
          const match = items.find((/** @type {any} */ item) => item.item_id === variantId || item.id === variantId);
          if (!match) return null;
          return {
            index: match.index,
            item_list_id: match.item_list_id,
            item_list_name: match.item_list_name,
          };
        } catch {
          return null;
        }
      })();

      // Persist list context per variant for remove_from_cart
      const variantId = eventObj?.data?.cartLine?.merchandise?.id;
      if (variantId && lastListData) {
        saveCartItemListContext(variantId, lastListData);
      }

      return {
        event: 'add_to_cart',
        add_to_cart_type:
          Object.keys(ADD_TO_CART_TYPE_MAP).find((pageType) =>
            ADD_TO_CART_TYPE_MAP[pageType].split(',').includes(globalValues.template?.name)
          ) || undefined,
        timestamp: new Date(eventObj?.timestamp)?.getTime() || undefined,
        _clear: true,
        ecommerce: {
          currency: eventObj?.data?.cartLine?.cost?.totalAmount?.currencyCode || undefined,
          value: eventObj?.data?.cartLine?.cost?.totalAmount?.amount || undefined,
          items: [mapCartLineToItem(eventObj?.data?.cartLine, lastListData || undefined)],
        },
      };
    },
    /**
     * @param {FormatterParams} params
     */
    product_removed_from_cart: ({ eventObj }) => {
      const variantId = eventObj?.data?.cartLine?.merchandise?.id;
      const listContext = variantId ? getCartItemListContext(variantId) : undefined;

      return {
        event: 'remove_from_cart',
        remove_from_cart_type: 'Cart',
        timestamp: new Date(eventObj?.timestamp)?.getTime() || undefined,
        _clear: true,
        ecommerce: {
          currency: eventObj?.data?.cartLine?.cost?.totalAmount?.currencyCode || undefined,
          value: eventObj?.data?.cartLine?.cost?.totalAmount?.amount || undefined,
          items: [mapCartLineToItem(eventObj?.data?.cartLine, listContext)],
        },
      };
    },
    /**
     * @param {FormatterParams} params
     */
    cart_viewed: ({ eventObj }) => {
      return {
        event: 'view_cart',
        timestamp: new Date(eventObj?.timestamp)?.getTime() || undefined,
        _clear: true,
        ecommerce: {
          currency: eventObj?.data?.cart?.cost?.totalAmount?.currencyCode || undefined,
          value: eventObj?.data?.cart?.cost?.totalAmount?.amount || undefined,
          items:
            eventObj?.data?.cart?.lines?.map(
              /** @param {any} line,  @param {number} index */
              (line, index) => ({
                item_id: line?.merchandise?.id || undefined,
                item_name:
                  `${line?.merchandise?.product?.title} - ${line?.merchandise?.title}`.replace(
                    ' - Default Title',
                    ''
                  ) || undefined,
                quantity: line?.quantity || undefined,
                price: line?.merchandise?.price?.amount || undefined,
                affiliation: line?.merchandise?.product?.type || undefined,
                coupon: undefined,
                discount: undefined,
                index: index,
                item_brand: 'Bundaberg',
                item_category: 'Products',
                item_category2: undefined,
                item_category3: undefined,
                item_category4: undefined,
                item_category5: undefined,
                item_list_id: undefined,
                item_list_name: undefined,
                item_variant: line?.merchandise?.title === 'Default Title' ? undefined : line?.merchandise?.title,
                location_id: undefined,
              })
            ) || undefined,
        },
      };
    },
    /**
     * @param {FormatterParams} params
     */
    search_submitted: ({ eventObj }) => {
      setTimeout(() => {
        window.dataLayerPush({
          event: 'search',
          data: {
            timestamp: new Date().toISOString(),
            data: {
              query: eventObj?.data?.searchResult?.query,
            },
          },
        });
      });

      const searchResultsViewedEvent = {
        event: 'view_item_list',
        item_list_type: 'Search Results',
        timestamp: new Date(eventObj?.timestamp)?.getTime() || undefined,
        _clear: true,
        ecommerce: {
          currency: eventObj?.data?.searchResult?.productVariants?.[0]?.price?.currencyCode || undefined,
          value:
            Number(
              [...(eventObj?.data?.searchResult?.productVariants || [])]
                .reduce((sum, v) => sum + (Number(v?.price?.amount) || 0), 0)
                .toFixed(2)
            ) || undefined,
          items:
            eventObj?.data?.searchResult?.productVariants?.map(
              /** @param {any} variant,  @param {number} index */
              (variant, index) => mapVariantToItem(variant, index)
            ) || undefined,
        },
      };

      localStorage.setItem('lastViewedCollectionData', JSON.stringify(searchResultsViewedEvent));

      return {
        ...searchResultsViewedEvent,
        ecommerce: {
          ...searchResultsViewedEvent.ecommerce,
          items: stripIdFromItems(searchResultsViewedEvent.ecommerce.items),
        },
      };
    },
    /**
     * @param {FormatterParams} params
     */
    collection_viewed: ({ eventObj }) => {
      const listContext = {
        item_list_id: globalValues.collection?.id || undefined,
        item_list_name: globalValues.collection?.title || undefined,
      };

      const collectionViewedEvent = {
        event: 'view_item_list',
        item_list_type: 'Product',
        timestamp: new Date(eventObj?.timestamp)?.getTime() || undefined,
        _clear: true,
        ecommerce: {
          currency: eventObj?.data?.collection?.productVariants?.[0]?.price?.currencyCode || undefined,
          value:
            Number(
              [...(eventObj?.data?.collection?.productVariants || [])]
                .reduce((sum, v) => sum + (Number(v?.price?.amount) || 0), 0)
                .toFixed(2)
            ) || undefined,
          items:
            eventObj?.data?.collection?.productVariants?.map(
              /** @param {any} variant,  @param {number} index */
              (variant, index) => mapVariantToItem(variant, index, listContext)
            ) || undefined,
        },
      };

      localStorage.setItem('lastViewedCollectionData', JSON.stringify(collectionViewedEvent));

      return {
        ...collectionViewedEvent,
        ecommerce: {
          ...collectionViewedEvent.ecommerce,
          items: stripIdFromItems(collectionViewedEvent.ecommerce.items),
        },
      };
    },
    /**
     * @param {FormatterParams} params
     */
    search: ({ eventObj }) => {
      return {
        event: 'search',
        search_term: undefined,
        timestamp: new Date(eventObj?.timestamp)?.getTime() || undefined,
      };
    },
    /**
     * @param {FormatterParams} params
     */
    cart_drawer_viewed: ({ eventObj }) => {
      return {
        event: 'view_cart',
        timestamp: new Date(eventObj?.timestamp)?.getTime() || undefined,
        _clear: true,
        ecommerce: {
          currency: eventObj?.data?.cart?.currency || undefined,
          value: eventObj?.data?.cart?.total_price || undefined,
          items:
            eventObj?.data?.cart?.items?.map(
              /** @param {any} line,  @param {number} index */
              (line, index) => ({
                item_id: line?.variant_id || undefined,
                item_name: `${line?.product_title} - ${line?.variant_title}`.replace(' - null', '') || undefined,
                quantity: line?.quantity || undefined,
                price: line?.final_line_price || undefined,
                affiliation: line?.product_type || undefined,
                coupon: line.discounts?.[0]?.title || undefined,
                discount: line.total_discount || undefined,
                index: index,
                item_brand: 'Bundaberg',
                item_category: 'Products',
                item_category2: undefined,
                item_category3: undefined,
                item_category4: undefined,
                item_category5: undefined,
                item_list_id: undefined,
                item_list_name: undefined,
                item_variant: line?.variant_title == null ? undefined : line?.variant_title,
                location_id: undefined,
              })
            ) || undefined,
        },
      };
    },
    /**
     * @param {FormatterParams} params
     */
    view_item_list: ({ eventObj }) => {
      return {
        event: 'view_item_list',
        item_list_type: 'Product',
        timestamp: new Date(eventObj?.timestamp)?.getTime() || undefined,
        _clear: true,
        ecommerce: {
          currency: eventObj?.data?.payload?.currency || undefined,
          value: eventObj?.data?.payload?.value || undefined,
          items: stripIdFromItems(eventObj?.data?.payload?.items),
        },
      };
    },
    /**
     * @param {FormatterParams} params
     */
    collection_selected_item: ({ eventObj }) => {
      return {
        event: 'select_item',
        select_item_type: 'Ecommerce',
        timestamp: new Date(eventObj?.timestamp)?.getTime() || undefined,
        _clear: true,
        ecommerce: {
          currency: eventObj?.data?.payload?.currency || undefined,
          value: eventObj?.data?.payload?.value || undefined,
          items: stripIdFromItems([eventObj?.data?.payload?.item || {}]),
        },
      };
    },
    /**
     * @param {FormatterParams} params
     */
    select_item: ({ eventObj }) => {
      return {
        event: 'select_item',
        select_item_type: 'Ecommerce',
        timestamp: new Date(eventObj?.timestamp)?.getTime() || undefined,
        _clear: true,
        ecommerce: {
          items: stripIdFromItems([eventObj?.data?.payload?.item || {}]),
        },
      };
    },
    /**
     * @param {FormatterParams} params
     */
    video_start: ({ eventObj }) => {
      return {
        event: 'video_start',
        video_title: eventObj?.data?.videoTitle || undefined,
        video_url: eventObj?.data?.videoSrc || undefined,
        timestamp: new Date(eventObj?.timestamp)?.getTime() || undefined,
      };
    },
    /**
     * @param {FormatterParams} params
     */
    video_progress: ({ eventObj }) => {
      return {
        event: 'video_progress',
        video_title: eventObj?.data?.videoTitle || undefined,
        video_url: eventObj?.data?.videoSrc || undefined,
        video_completion: eventObj?.data?.progress ? eventObj?.data?.progress + '%' : undefined,
        timestamp: new Date(eventObj?.timestamp)?.getTime() || undefined,
      };
    },
    /**
     * @param {FormatterParams} params
     */
    video_complete: ({ eventObj }) => {
      return {
        event: 'video_complete',
        video_title: eventObj?.data?.videoTitle || undefined,
        video_url: eventObj?.data?.videoSrc || undefined,
        video_completion: eventObj?.data?.progress ? eventObj?.data?.progress + '%' : undefined,
        timestamp: new Date(eventObj?.timestamp)?.getTime() || undefined,
      };
    },
    /**
     * @param {FormatterParams} params
     */
    form_start: ({ eventObj }) => {
      return {
        event: 'form_start',
        form_type: eventObj?.data?.formType || undefined,
        timestamp: new Date(eventObj?.timestamp)?.getTime() || undefined,
      };
    },
    /**
     * @param {FormatterParams} params
     */
    form_submit: ({ eventObj }) => {
      return {
        event: 'form_submit',
        form_type: eventObj?.data?.formType || undefined,
        status: eventObj?.data?.status || undefined,
        timestamp: new Date(eventObj?.timestamp)?.getTime() || undefined,
      };
    },
    /**
     * @param {FormatterParams} params
     */
    newsletter_signup: ({ eventObj }) => {
      return {
        event: 'newsletter_signup',
        form_type: eventObj?.data?.formType || undefined,
        status: eventObj?.data?.status || undefined,
        timestamp: new Date(eventObj?.timestamp)?.getTime() || undefined,
        newsletter_optin_type: eventObj?.data?.newsletterOptinType || undefined,
      };
    },
    /**
     * @param {FormatterParams} params
     */
    content_interaction: ({ eventObj }) => {
      return {
        event: 'content_interaction',
        journey_type: eventObj?.data?.journeyType || undefined,
        interaction_type: globalValues.pageTitle || undefined,
        interaction_name: eventObj?.data?.interactionName || undefined,
        timestamp: new Date(eventObj?.timestamp)?.getTime() || undefined,
      };
    },
    /**
     * @param {FormatterParams} params
     */
    outbound_click: ({ eventObj }) => {
      return {
        event: 'outbound_click',
        link_name: eventObj?.data?.linkName || undefined,
        link_url: eventObj?.data?.linkUrl || undefined,
        outbound_link_type: eventObj?.data?.outboundLinkType || undefined,
        timestamp: new Date(eventObj?.timestamp)?.getTime() || undefined,
      };
    },
    /**
     * @param {FormatterParams} params
     */
    navigation_click: ({ eventObj }) => {
      return {
        event: 'navigation_click',
        journey_type: eventObj?.data?.journeyType || undefined,
        navigation_type: eventObj?.data?.navigationType || undefined,
        interaction_name: eventObj?.data?.interactionName || undefined,
        timestamp: new Date(eventObj?.timestamp)?.getTime() || undefined,
      };
    },
    /**
     * @param {FormatterParams} params
     */
    scroll: ({ eventObj }) => {
      return {
        event: 'scroll',
        scroll_percentage: eventObj.data?.scrollPercentage || undefined,
        timestamp: new Date(eventObj?.timestamp)?.getTime() || undefined,
      };
    },
  });
})();
